import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DrawIcon from '@mui/icons-material/Draw';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PortalLayout from './PortalLayout';
import PortalStatusPill from './PortalStatusPill';
import { portalCardSx } from './portalStyles';
import PortalContractService from '../../services/PortalContractService';

const STATUS_META = {
    SIGNING:           { label: 'Awaiting Signature', color: '#F4A261' },
    CHANGES_REQUESTED: { label: 'Changes Requested',  color: '#E0C454' },
    ACTIVE:            { label: 'Active',              color: '#2EC4B6' },
    TERMINATED:        { label: 'Terminated',          color: '#B0B0B0' },
};

function StatusChip({ status }) {
    const meta = STATUS_META[status];
    return <PortalStatusPill status={status} label={meta ? meta.label : undefined} />;
}

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatAmount(val) {
    if (val == null) return '—';
    return 'CHF ' + Number(val).toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDateTime(d) {
    if (!d) return '';
    return new Date(d).toLocaleString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
}

// ─── Notes thread ─────────────────────────────────────────────────────────────

function NoteItem({ note, contractId, onUpdated }) {
    const [editing, setEditing] = useState(false);
    const [editValue, setEditValue] = useState(note.content || '');
    const [saving, setSaving] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const canEdit = !note.deleted && note.authorType === 'CUSTOMER';

    const handleSave = async () => {
        if (!editValue.trim()) return;
        setSaving(true);
        try {
            await PortalContractService.editNote(contractId, note.id, editValue.trim());
            setEditing(false);
            onUpdated();
        } catch (e) {
            // ignore — parent will not refresh on error
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setConfirmDelete(false);
        try {
            await PortalContractService.deleteNote(contractId, note.id);
            onUpdated();
        } catch (e) {
            // ignore
        }
    };

    const isCrm = note.authorType === 'CRM_USER';

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isCrm ? 'row' : 'row-reverse',
                gap: 1.5,
                mb: 2,
            }}
        >
            {/* Avatar-like indicator */}
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: isCrm ? '#427898' : '#2e7d32',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.5,
                }}
            >
                <Typography sx={{ color: 'white', fontSize: 12, fontWeight: 700 }}>
                    {isCrm ? 'T' : 'Y'}
                </Typography>
            </Box>

            <Box sx={{ flex: 1, maxWidth: '80%' }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={700} sx={{ color: '#475569' }}>
                        {isCrm ? 'Your team' : 'You'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                        {formatDateTime(note.createdAt)}
                    </Typography>
                    {note.editedAt && !note.deleted && (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                            (edited)
                        </Typography>
                    )}
                </Box>

                {/* Bubble */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 1.5,
                        backgroundColor: isCrm ? '#f1f5f9' : '#e8f4e8',
                        border: '1px solid',
                        borderColor: isCrm ? '#e2e8f0' : '#c8e6c9',
                        borderRadius: 2,
                        position: 'relative',
                    }}
                >
                    {note.deleted ? (
                        <Typography
                            variant="body2"
                            sx={{ color: '#94a3b8', fontStyle: 'italic' }}
                        >
                            [deleted]
                        </Typography>
                    ) : editing ? (
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                size="small"
                                autoFocus
                                sx={{ mb: 1 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    disabled={saving || !editValue.trim()}
                                    onClick={handleSave}
                                    sx={{ backgroundColor: '#427898', textTransform: 'none', fontSize: 12 }}
                                    startIcon={saving ? <CircularProgress size={12} /> : <CheckIcon />}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="small"
                                    onClick={() => { setEditing(false); setEditValue(note.content); }}
                                    sx={{ textTransform: 'none', fontSize: 12, color: '#64748b' }}
                                    startIcon={<CloseIcon />}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Typography variant="body2" sx={{ color: '#1a2332', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {note.content}
                        </Typography>
                    )}

                    {/* Edit/Delete actions */}
                    {canEdit && !editing && (
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => { setEditValue(note.content); setEditing(true); }}>
                                    <EditIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => setConfirmDelete(true)}>
                                    <DeleteOutlineIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Paper>
            </Box>

            {/* Delete confirm dialog */}
            <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Delete Note</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Are you sure you want to delete this note? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" sx={{ textTransform: 'none' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default function PortalContractDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);

    // Request changes dialog
    const [changesDialogOpen, setChangesDialogOpen] = useState(false);
    const [changesComment, setChangesComment] = useState('');
    const [changesError, setChangesError] = useState(null);

    // Notes thread
    const [notes, setNotes] = useState([]);
    const [loadingNotes, setLoadingNotes] = useState(true);
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const notesEndRef = useRef(null);

    const fetchNotes = useCallback(() => {
        PortalContractService.getNotes(id)
            .then(data => setNotes(data || []))
            .catch(() => {}) // notes are non-critical
            .finally(() => setLoadingNotes(false));
    }, [id]);

    useEffect(() => {
        PortalContractService.getContract(id)
            .then(setContract)
            .catch(() => setError('Failed to load contract. Please try again.'))
            .finally(() => setLoading(false));

        let url;
        PortalContractService.downloadPdf(id)
            .then(response => {
                url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                setPdfUrl(url);
            })
            .catch(() => {});
        return () => { if (url) URL.revokeObjectURL(url); };
    }, [id]);

    useEffect(() => {
        fetchNotes();
    }, [fetchNotes]);

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) return;
        setAddingNote(true);
        try {
            await PortalContractService.addNote(id, newNoteContent.trim());
            setNewNoteContent('');
            fetchNotes();
            setTimeout(() => notesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        } catch (e) {
            // ignore
        } finally {
            setAddingNote(false);
        }
    };

    const handleDownloadPdf = async () => {
        try {
            const response = await PortalContractService.downloadPdf(id);
            const filename = contract?.displayName ? `${contract.displayName}.pdf` : 'contract.pdf';
            const blobUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(blobUrl);
        } catch {
            // silent — the user can retry
        }
    };

    const handleOpenRequestChanges = () => {
        setChangesComment('');
        setChangesError(null);
        setChangesDialogOpen(true);
    };

    const handleSubmitChanges = async () => {
        if (!changesComment.trim()) {
            setChangesError('Please enter a comment describing the changes you need.');
            return;
        }
        setSubmitting(true);
        setChangesError(null);
        try {
            await PortalContractService.requestChanges(id, changesComment.trim());
            const updated = await PortalContractService.getContract(id);
            setContract(updated);
            setChangesDialogOpen(false);
            setChangesComment('');
        } catch {
            setChangesError('Failed to submit your request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <PortalLayout>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                    <CircularProgress sx={{ color: '#427898' }} />
                </Box>
            </PortalLayout>
        );
    }

    if (error || !contract) {
        return (
            <PortalLayout>
                <Alert severity="error" sx={{ borderRadius: 2 }}>{error || 'Contract not found.'}</Alert>
            </PortalLayout>
        );
    }

    const { status, signingSubState, customerSigningUrl, latestRevisionComment } = contract;
    const isCustomerTurn = status === 'SIGNING' && signingSubState === 'CUSTOMER_TURN';
    const isCompanyTurn  = status === 'SIGNING' && signingSubState === 'COMPANY_TURN';

    return (
        <PortalLayout>
            {/* ── Sticky header ── */}
            <Box
                sx={{
                    ...portalCardSx,
                    position: 'sticky',
                    top: 12,
                    zIndex: 10,
                    px: 2.5,
                    py: 1.25,
                    mb: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}
            >
                <Button
                    size="small"
                    startIcon={<ArrowBackIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => navigate('/portal/contracts')}
                    sx={{ color: '#64748B', fontWeight: 500, textTransform: 'none', fontSize: 13, px: 1, minWidth: 0, flexShrink: 0, '&:hover': { bgcolor: '#F1F5F9' } }}
                >
                    Contracts
                </Button>
                <Divider orientation="vertical" flexItem sx={{ height: 16, alignSelf: 'center' }} />
                <Typography noWrap sx={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>
                    {contract.displayName}
                </Typography>
                <StatusChip status={status} />
                <Box sx={{ flex: 1 }} />
                <Button
                    size="small"
                    startIcon={<DownloadIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={handleDownloadPdf}
                    sx={{ color: '#427898', fontWeight: 600, textTransform: 'none', fontSize: 13, flexShrink: 0, '&:hover': { bgcolor: '#F0F7FF' } }}
                >
                    PDF
                </Button>
            </Box>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>

                {/* ── Main content ── */}
                <Box sx={{ flex: 1, minWidth: 0 }}>

                    {/* Contract details card */}
                    <Paper elevation={0} sx={{ ...portalCardSx, p: 2.5, mb: 2 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}
                        >
                            Details
                        </Typography>
                        <Stack spacing={1.5}>
                            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Start Date</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatDate(contract.startDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">End Date</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatDate(contract.endDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Total Value</Typography>
                                    <Typography variant="body2" fontWeight={600}>{formatAmount(contract.totalValue)}</Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </Paper>

                    {/* ── Status-based content ── */}

                    {/* SIGNING — company turn */}
                    {isCompanyTurn && (
                        <Alert
                            severity="info"
                            icon={<InfoOutlinedIcon />}
                            sx={{ borderRadius: 2, mb: 2 }}
                        >
                            This contract is awaiting an internal signature. You will be notified when it's your turn to sign.
                        </Alert>
                    )}

                    {/* SIGNING — customer turn */}
                    {isCustomerTurn && (
                        <Paper elevation={0} sx={{ ...portalCardSx, p: 2.5, mb: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: '#1a2332' }}>
                                Your signature is required
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#4a5568', mb: 2 }}>
                                Please review the contract document and sign it, or request changes if anything needs to be adjusted.
                            </Typography>
                            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                                {customerSigningUrl && (
                                    <Button
                                        variant="contained"
                                        startIcon={<DrawIcon />}
                                        onClick={() => window.open(customerSigningUrl, '_blank')}
                                        sx={{
                                            backgroundColor: '#427898',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': { backgroundColor: '#355f77' },
                                        }}
                                    >
                                        Sign Document
                                    </Button>
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<EditNoteIcon />}
                                    onClick={handleOpenRequestChanges}
                                    sx={{
                                        borderColor: '#427898',
                                        color: '#427898',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': { backgroundColor: '#42789810' },
                                    }}
                                >
                                    Request Changes
                                </Button>
                            </Stack>
                        </Paper>
                    )}

                    {/* CHANGES_REQUESTED */}
                    {status === 'CHANGES_REQUESTED' && (
                        <Paper elevation={0} sx={{ ...portalCardSx, p: 2.5, mb: 2 }}>
                            <Alert severity="warning" sx={{ borderRadius: 1.5, mb: latestRevisionComment ? 2 : 0 }}>
                                Your change request is being reviewed. We will get back to you shortly.
                            </Alert>
                            {latestRevisionComment && (
                                <>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                                        Your comment
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5, color: '#4a5568', whiteSpace: 'pre-wrap' }}>
                                        {latestRevisionComment}
                                    </Typography>
                                </>
                            )}
                        </Paper>
                    )}

                    {/* ACTIVE */}
                    {status === 'ACTIVE' && (
                        <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                            This contract is active and fully signed.
                        </Alert>
                    )}

                    {/* TERMINATED */}
                    {status === 'TERMINATED' && (
                        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2, mb: 2 }}>
                            This contract has been terminated.
                        </Alert>
                    )}

                    {/* PDF preview */}
                    {pdfUrl && (
                        <Paper elevation={0} sx={{ ...portalCardSx, mb: 2 }}>
                            <iframe
                                src={pdfUrl}
                                title="Contract PDF"
                                width="100%"
                                height="720px"
                                style={{ border: 'none', display: 'block' }}
                            />
                        </Paper>
                    )}

                    {/* Messages */}
                    <Paper elevation={0} sx={{ ...portalCardSx, p: 3 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}
                        >
                            Messages
                        </Typography>

                        {/* Notes thread */}
                        <Box sx={{ mb: 2, maxHeight: 480, overflowY: 'auto', pr: 1 }}>
                            {loadingNotes ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} sx={{ color: '#427898' }} />
                                </Box>
                            ) : notes.length === 0 ? (
                                <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center', py: 3 }}>
                                    No messages yet. Be the first to leave one.
                                </Typography>
                            ) : (
                                notes.map((note) => (
                                    <NoteItem
                                        key={note.id}
                                        note={note}
                                        contractId={id}
                                        onUpdated={fetchNotes}
                                    />
                                ))
                            )}
                            <div ref={notesEndRef} />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Add note */}
                        <Box>
                            <TextField
                                fullWidth
                                multiline
                                minRows={2}
                                placeholder="Write a message..."
                                value={newNoteContent}
                                onChange={(e) => setNewNoteContent(e.target.value)}
                                size="small"
                                sx={{ mb: 1.5 }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.ctrlKey) handleAddNote();
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    disabled={addingNote || !newNoteContent.trim()}
                                    onClick={handleAddNote}
                                    sx={{
                                        backgroundColor: '#427898',
                                        textTransform: 'none',
                                        fontWeight: 600,
                                        '&:hover': { backgroundColor: '#356380' },
                                    }}
                                    startIcon={addingNote ? <CircularProgress size={14} color="inherit" /> : null}
                                >
                                    Send
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Box>

                {/* ── Sidebar / Actions ── */}
                <Box sx={{ width: { xs: '100%', md: 240 }, flexShrink: 0 }}>
                    <Paper elevation={0} sx={{ ...portalCardSx, p: 2 }}>
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="text.secondary"
                            sx={{ mb: 1.5, textTransform: 'uppercase', letterSpacing: 1, fontSize: 11 }}
                        >
                            Actions
                        </Typography>
                        <Stack spacing={1}>
                            <Button
                                fullWidth
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={handleDownloadPdf}
                                sx={{
                                    borderColor: '#427898',
                                    color: '#427898',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    '&:hover': { backgroundColor: '#42789810' },
                                }}
                            >
                                Download PDF
                            </Button>

                            {isCustomerTurn && customerSigningUrl && (
                                <>
                                    <Divider />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<DrawIcon />}
                                        onClick={() => window.open(customerSigningUrl, '_blank')}
                                        sx={{
                                            backgroundColor: '#427898',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': { backgroundColor: '#355f77' },
                                        }}
                                    >
                                        Sign Document
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<EditNoteIcon />}
                                        onClick={handleOpenRequestChanges}
                                        sx={{
                                            borderColor: '#427898',
                                            color: '#427898',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            '&:hover': { backgroundColor: '#42789810' },
                                        }}
                                    >
                                        Request Changes
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </Paper>
                </Box>
            </Box>

            {/* ── Request Changes Dialog ── */}
            <Dialog
                open={changesDialogOpen}
                onClose={() => !submitting && setChangesDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>Request Changes</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#4a5568', mb: 2 }}>
                        Please describe what changes you would like to see in this contract.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        label="Comment"
                        value={changesComment}
                        onChange={(e) => {
                            setChangesComment(e.target.value);
                            if (changesError) setChangesError(null);
                        }}
                        error={!!changesError}
                        helperText={changesError}
                        disabled={submitting}
                        sx={{ mt: 0.5 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setChangesDialogOpen(false)}
                        disabled={submitting}
                        sx={{ color: '#64748b', textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmitChanges}
                        disabled={submitting || !changesComment.trim()}
                        sx={{
                            backgroundColor: '#427898',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: '#355f77' },
                        }}
                    >
                        {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </PortalLayout>
    );
}
